import axios from "axios";
import { DateitUserMetadata, PubComment, PubEvent, PubEventActivity, PubEventMedia, PubGuest, PubInviteLink, PubPost, Features, CoverPhotoManifest, EmojiManifest, EventType, PubReactionSummary } from "./data/Responses";
import {
    NewEventFromUserRequest,
    UpdateEventRequest,
    InviteRequest,
    InviteExistingRequest,
    ReactionRequest,
    UpdateAttendanceRequest,
    EventsRequest,
    EventRequest,
    SwapUserRequest,
    GuestActionRequest,
    DeleteEventRequest,
    NameRequest,
    FcmTokenRequest,
    LeaveEventRequest,
    PostRequest,
    DeletePostRequest,
    PostsRequest,
    PostCommentRequest,
    DeleteCommentRequest,
    GetCommentRequest,
    GetCommentsRequest,
    EditCommentRequest,
    EditPostRequest,
    MediaContext,
    EventMediaRequest,
    DeleteEventMediaRequest,
    AllPhotosRequest,
    ActivityLogRequest,
    VerifyPhoneRequest,
    UpgradeEventRequest,
    UpgradeAccountRequest,
    LikeEventMediaRequest,
} from "./data/Requests";
import { Constants } from "./Constants";
import { getAuth } from "firebase/auth";
import Preferences from "./data/Preferences";
import { v4 as uuidv4 } from "uuid";

export default class Api {
    public static sessionId = uuidv4();

    async getConfig() {
        const locale = new Intl.Locale(navigator.language);
        if (getAuth().currentUser == null) {
            return {
                headers: {
                    platform: "web",
                    language: navigator.language || "en",
                    region: locale.region || "US",
                    "session-id": Api.sessionId,
                    "api-version": 4,
                    version: Constants.version,
                },
            };
        }

        const idToken = await getAuth().currentUser?.getIdToken();

        return {
            headers: {
                platform: "web",
                region: locale.region || "US",
                language: navigator.language || "en",
                Authorization: "Bearer " + idToken,
                "session-id": Api.sessionId,
                "api-version": 4,
                version: Constants.version,
            },
        };
    }

    async firebaseLogin(): Promise<{}> {
        const resp = await axios.post("/api/firebaseLogin", {}, await this.getConfig());
        return resp.data;
    }

    async updateAttendance(request: UpdateAttendanceRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/updateAttendance", request, await this.getConfig());
        return resp.data;
    }

    async createEvent(request: NewEventFromUserRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/createEventFromUser", request, await this.getConfig());
        return resp.data;
    }

    async updateEvent(request: UpdateEventRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/updateEvent", request, await this.getConfig());
        return resp.data;
    }

    async deleteEvent(request: DeleteEventRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/deleteEvent", request, await this.getConfig());
        return resp.data;
    }

    async leaveEvent(request: LeaveEventRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/leaveEvent", request, await this.getConfig());
        return resp.data;
    }

    async getPosts(eventId: string, accessCode?: string): Promise<PubPost[]> {
        const resp = await axios.post<PubPost[]>("/api/posts", { eventId, accessCode }, await this.getConfig());
        return resp.data;
    }

    async getEvents(request: EventsRequest): Promise<PubEvent[]> {
        const resp = await axios.get<PubEvent[]>("/api/events", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async getEvent(request: EventRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/event", request, await this.getConfig());
        return resp.data;
    }

    async getUserMetadata(): Promise<DateitUserMetadata> {
        const resp = await axios.post<DateitUserMetadata>("/api/userMetadata", {}, { ...(await this.getConfig()), timeout: 30_000 });
        return resp.data;
    }

    async invitableUsers(): Promise<PubGuest[]> {
        const resp = await axios.post<PubGuest[]>("/api/invitableUsers", {}, await this.getConfig());
        return resp.data;
    }

    async inviteExisting(eventId: string, guestIds: string[]): Promise<{}> {
        const resp = await axios.post<{}>("/api/inviteExisting", { eventId, guestIds }, { ...(await this.getConfig()), timeout: 30_000 });
        return resp.data;
    }

    async removeGuest(request: GuestActionRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/removeGuest", request, await this.getConfig());
        return resp.data;
    }

    async makeHost(request: GuestActionRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/makeHost", request, await this.getConfig());
        return resp.data;
    }

    async removeHost(request: GuestActionRequest): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/removeHost", request, await this.getConfig());
        return resp.data;
    }

    async newComment(request: PostCommentRequest): Promise<PubComment> {
        const resp = await axios.post<PubComment>("/api/comment", request, await this.getConfig());
        return resp.data;
    }

    async newPost(request: PostRequest): Promise<PubPost> {
        const resp = await axios.post<PubPost>("/api/newPost", request, await this.getConfig());
        return resp.data;
    }

    async inviteUser(request: InviteRequest): Promise<PubInviteLink> {
        const resp = await axios.post<PubInviteLink>("/api/invite", request, await this.getConfig());
        return resp.data;
    }

    async changeEventType(eventId: string, type: EventType): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/updateEvent", { eventId, type }, await this.getConfig());
        return resp.data;
    }

    async changePlusOnes(eventId: string, val: number): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>("/api/updateEvent", { eventId, guestInviteLimit: val }, await this.getConfig());
        return resp.data;
    }

    async getEventMedia(request: EventMediaRequest): Promise<PubEventMedia[]> {
        const resp = await axios.get<PubEventMedia[]>("/api/eventMedia", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async linkEmail(eventId: string, email: string, accessCode: string, notificationsEnabled: boolean): Promise<{}> {
        const resp = await axios.post<{}>("/api/linkEmail", { eventId, email, accessCode, notificationsEnabled }, await this.getConfig());
        return resp.data;
    }

    async linkPhone(eventId: string, phone: string, accessCode: string): Promise<{}> {
        const resp = await axios.post<{}>("/api/linkPhone", { eventId, phone, accessCode }, await this.getConfig());
        return resp.data;
    }

    async customizeRsvp(eventId: string, yes?: string, no?: string, maybe?: string, rsvp?: string): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>(
            "/api/updateEvent",
            {
                eventId,
                customYes: yes,
                customNo: no,
                customMaybe: maybe,
                customRSVP: rsvp,
            },
            await this.getConfig(),
        );
        return resp.data;
    }

    async preferences(): Promise<Preferences> {
        const resp = await axios.get<Preferences>("/api/preferences", { ...(await this.getConfig()), timeout: 30_000 });
        return resp.data;
    }

    async updatePreferences(username: string, emailNotifications: boolean): Promise<{}> {
        const resp = await axios.post<{}>(
            "/api/preferences",
            {
                username,
                emailNotifications,
            },
            await this.getConfig(),
        );
        return resp.data;
    }

    async swapUser(request: SwapUserRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/swapUser", request, await this.getConfig());
        return resp.data;
    }

    async cancelEvent(eventId: string): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>(
            "/api/updateEvent",
            {
                eventId,
                cancel: true,
            },
            await this.getConfig(),
        );
        return resp.data;
    }

    async unCancelEvent(eventId: string): Promise<PubEvent> {
        const resp = await axios.post<PubEvent>(
            "/api/updateEvent",
            {
                eventId,
                cancel: false,
            },
            await this.getConfig(),
        );
        return resp.data;
    }

    async eventActivity(request: ActivityLogRequest): Promise<PubEventActivity[]> {
        const resp = await axios.get<PubEventActivity[]>("/api/eventActivity", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async verifyPhone(request: VerifyPhoneRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/verifyPhone", request, { ...(await this.getConfig()), timeout: 30_000 });
        return resp.data;
    }

    async deletePost(request: DeletePostRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/deletePost", request, await this.getConfig());
        return resp.data;
    }

    async deleteComment(request: DeleteCommentRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/deleteComment", request, await this.getConfig());
        return resp.data;
    }

    async getComment(request: GetCommentRequest): Promise<PubComment> {
        const resp = await axios.get<PubComment>("/api/comment", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async getComments(request: GetCommentsRequest): Promise<PubComment[]> {
        const resp = await axios.get<PubComment[]>("/api/comments", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async editComment(request: EditCommentRequest): Promise<PubComment> {
        const resp = await axios.post<PubComment>("/api/editComment", request, await this.getConfig());
        return resp.data;
    }

    async editPost(request: EditPostRequest): Promise<PubPost> {
        const resp = await axios.post<PubPost>("/api/editPost", request, await this.getConfig());
        return resp.data;
    }

    async features(): Promise<Features> {
        const resp = await axios.get<Features>("/api/features", await this.getConfig());
        return resp.data;
    }

    async coverPhotosManifest(): Promise<CoverPhotoManifest> {
        const resp = await axios.get<CoverPhotoManifest>("/api/coverPhotosManifest", await this.getConfig());
        return resp.data;
    }

    async emojiManifest(): Promise<EmojiManifest> {
        const resp = await axios.get<EmojiManifest>("/api/emojiManifest", await this.getConfig());
        return resp.data;
    }

    async uploadMedia(context: MediaContext, data: FormData, onProgress?: (progress: number) => void): Promise<{}> {
        const resp = await axios.post<{}>("/api/uploadMedia", data, {
            params: context,
            headers: (await this.getConfig()).headers,
            onUploadProgress: onProgress
                ? (progressEvent) => {
                      if (progressEvent.total) {
                          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                          onProgress(percentCompleted);
                      }
                  }
                : undefined,
        });
        return resp.data;
    }

    async deleteEventMedia(request: DeleteEventMediaRequest): Promise<{}> {
        const resp = await axios.delete<{}>("/api/eventMedia", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async likeEventMedia(request: LikeEventMediaRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/likeEventMedia", request, await this.getConfig());
        return resp.data;
    }

    async unlikeEventMedia(request: LikeEventMediaRequest): Promise<{}> {
        const resp = await axios.delete<{}>("/api/likeEventMedia", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async getAllPhotos(request: AllPhotosRequest): Promise<PubEventMedia[]> {
        const resp = await axios.get<PubEventMedia[]>("/api/allMedia", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }

    async upgradeEvent(request: UpgradeEventRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/upgradeEvent", request, await this.getConfig());
        return resp.data;
    }

    async upgradeAccount(request: UpgradeAccountRequest): Promise<{}> {
        const resp = await axios.post<{}>("/api/upgradeAccount", request, await this.getConfig());
        return resp.data;
    }

    async deleteAccount(): Promise<{}> {
        const resp = await axios.post<{}>("/api/deleteAccount", {}, await this.getConfig());
        return resp.data;
    }

    async addReaction(request: ReactionRequest): Promise<PubReactionSummary> {
        const resp = await axios.post<PubReactionSummary>("/api/reaction", request, await this.getConfig());
        return resp.data;
    }

    async deleteReaction(request: ReactionRequest): Promise<{}> {
        const resp = await axios.delete<{}>("/api/reaction", {
            params: request,
            headers: (await this.getConfig()).headers,
        });
        return resp.data;
    }
}
